import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Sparkles,
  Calendar as CalendarIcon,
  ListChecks,
  Sparkle,
  AlertTriangle,
} from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseYMD(s) {
  if (!s || typeof s !== "string") return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function formatLongDate(s) {
  const d = parseYMD(s);
  if (!d) return s;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function buildMonthGrid(viewDate) {
  // Returns 6 weeks of dates (Mon..Sun) covering the month
  const first = startOfMonth(viewDate);
  const firstWeekday = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(first);
  start.setDate(first.getDate() - firstWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function EntryDialog({ open, onOpenChange, initial, onSave }) {
  const [date, setDate] = useState(initial?.date || ymd(new Date()));
  const [label, setLabel] = useState(initial?.label || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(initial?.date || ymd(new Date()));
      setLabel(initial?.label || "");
      setNotes(initial?.notes || "");
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !date) {
      toast.error("Date and label are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ date, label: label.trim(), notes: notes.trim() });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="calendar-entry-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit date" : "Add a custom date"}</DialogTitle>
          <DialogDescription>
            For things like a meet &amp; greet, a debate, a fundraiser, or a deadline you've personally confirmed.
            <span className="block mt-2 text-flag-red font-semibold">
              Heads up: registration, early-voting, and ballot deadlines vary by jurisdiction — please verify those yourself before entering.
            </span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-800">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="mt-1 h-11 text-base"
              data-testid="calendar-entry-date-input"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-800">Label</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              maxLength={200}
              placeholder='e.g. "Meet &amp; greet at Wilson Lodge"'
              className="mt-1 h-11 text-base"
              data-testid="calendar-entry-label-input"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-800">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Anything you'd want the draft to mention — context, talking points, who's hosting…"
              className="mt-1 text-base"
              data-testid="calendar-entry-notes-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="calendar-entry-cancel-btn"
              className="font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="calendar-entry-save-btn"
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            >
              {saving ? "Saving…" : initial?.id ? "Save changes" : "Add to calendar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewDate, setViewDate] = useState(startOfMonth(new Date()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: e }, { data: s }] = await Promise.all([
        api.get("/calendar"),
        api.get("/calendar/suggested"),
      ]);
      setEntries(e || []);
      setSuggested(s || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entriesByDate = useMemo(() => {
    const m = {};
    for (const e of entries) {
      (m[e.date] = m[e.date] || []).push(e);
    }
    return m;
  }, [entries]);

  const upcoming = useMemo(() => {
    const today = ymd(new Date());
    return entries.filter((e) => e.date >= today);
  }, [entries]);

  const past = useMemo(() => {
    const today = ymd(new Date());
    return entries.filter((e) => e.date < today);
  }, [entries]);

  const toggleSuggested = async (s) => {
    try {
      if (s.enabled) {
        await api.delete(`/calendar/suggested/${s.id}`);
      } else {
        await api.post(`/calendar/suggested/${s.id}`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveEntry = async (payload) => {
    if (editing?.id) {
      await api.put(`/calendar/${editing.id}`, payload);
    } else {
      await api.post("/calendar", payload);
    }
    setEditing(null);
    await load();
    toast.success(editing?.id ? "Updated" : "Added");
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/calendar/${id}`);
      setEntries((prev) => prev.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const draftFor = (entry) => {
    const niceDate = formatLongDate(entry.date);
    const parts = [`Date: ${niceDate}`, `Occasion: ${entry.label}`];
    if (entry.theme) parts.push(`Suggested angle: ${entry.theme}`);
    if (entry.notes) parts.push(`Notes: ${entry.notes}`);
    const moment = parts.join("\n");
    navigate("/generator", { state: { prefill: moment, source: "calendar", entryId: entry.id } });
  };

  const cells = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const todayStr = ymd(new Date());

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Scheduler</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Plan your campaign cadence</h1>
          <p className="text-slate-600 mt-2">Mark the moments worth posting about. Toggle the holidays you'll observe. Add your own dates. Hit “Draft a post for this” when it's time.</p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          data-testid="calendar-add-btn"
          className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add a date
        </Button>
      </div>

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex gap-2" data-testid="calendar-disclaimer">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">A note on election deadlines.</span> We don't pre-fill voter-registration, early-voting, or mail-in-ballot dates — those vary by jurisdiction and change. Look them up from your local board of elections and add them yourself.
        </div>
      </div>

      {error && (
        <div className="mt-4 text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
      )}

      <Tabs defaultValue="upcoming" className="mt-8">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="upcoming" data-testid="calendar-tab-upcoming"><ListChecks className="h-4 w-4 mr-1.5" /> Upcoming</TabsTrigger>
          <TabsTrigger value="month" data-testid="calendar-tab-month"><CalendarIcon className="h-4 w-4 mr-1.5" /> Month</TabsTrigger>
          <TabsTrigger value="suggested" data-testid="calendar-tab-suggested"><Sparkle className="h-4 w-4 mr-1.5" /> Suggested</TabsTrigger>
        </TabsList>

        {/* Upcoming list */}
        <TabsContent value="upcoming" className="mt-6">
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="calendar-upcoming-empty">
              <p className="text-slate-600">Nothing on the calendar yet.</p>
              <p className="text-slate-500 text-sm mt-1">Toggle on a suggested holiday or add a custom date to get started.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="calendar-upcoming-list">
              {upcoming.map((e) => (
                <EntryRow key={e.id} entry={e} onDraft={draftFor} onEdit={() => { setEditing(e); setDialogOpen(true); }} onDelete={removeEntry} />
              ))}
            </ul>
          )}

          {past.length > 0 && (
            <details className="mt-8" data-testid="calendar-past-details">
              <summary className="cursor-pointer text-sm font-semibold text-slate-500 hover:text-flag-navy">
                Past dates ({past.length})
              </summary>
              <ul className="mt-3 space-y-3 opacity-70">
                {past.map((e) => (
                  <EntryRow key={e.id} entry={e} onDraft={draftFor} onEdit={() => { setEditing(e); setDialogOpen(true); }} onDelete={removeEntry} />
                ))}
              </ul>
            </details>
          )}
        </TabsContent>

        {/* Month grid */}
        <TabsContent value="month" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setViewDate((d) => addMonths(d, -1))}
                className="h-9 w-9 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                aria-label="Previous month"
                data-testid="calendar-prev-month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="font-display text-xl font-bold text-flag-navy" data-testid="calendar-month-label">
                {viewDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewDate(startOfMonth(new Date()))}
                  className="h-9 px-3 text-sm font-semibold rounded-md border border-slate-200 hover:bg-slate-50"
                  data-testid="calendar-today-btn"
                >
                  Today
                </button>
                <button
                  onClick={() => setViewDate((d) => addMonths(d, 1))}
                  className="h-9 w-9 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                  aria-label="Next month"
                  data-testid="calendar-next-month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden text-xs sm:text-sm">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-slate-50 px-2 py-2 font-bold text-slate-600 text-center">{w}</div>
              ))}
              {cells.map((d) => {
                const dStr = ymd(d);
                const inMonth = d.getMonth() === viewDate.getMonth();
                const isToday = dStr === todayStr;
                const dayEntries = entriesByDate[dStr] || [];
                return (
                  <div
                    key={dStr}
                    className={`min-h-[88px] sm:min-h-[110px] bg-white p-1.5 flex flex-col ${inMonth ? "" : "bg-slate-50/60 text-slate-400"}`}
                    data-testid={`calendar-cell-${dStr}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-flag-red text-white" : ""}`}>
                        {d.getDate()}
                      </span>
                    </div>
                    <div className="mt-1 space-y-1 overflow-hidden">
                      {dayEntries.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          onClick={() => draftFor(e)}
                          title={`${e.label} — Draft a post for this`}
                          data-testid={`calendar-cell-entry-${e.id}`}
                          className={`block w-full text-left px-1.5 py-0.5 rounded text-[11px] sm:text-xs leading-tight font-semibold truncate transition-colors ${
                            e.kind === "suggested"
                              ? "bg-flag-navy/10 text-flag-navy hover:bg-flag-navy/15"
                              : "bg-flag-red/10 text-flag-red hover:bg-flag-red/15"
                          }`}
                        >
                          {e.label}
                        </button>
                      ))}
                      {dayEntries.length > 3 && (
                        <div className="text-[10px] text-slate-500 font-semibold px-1.5">+{dayEntries.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Suggested */}
        <TabsContent value="suggested" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
            <h2 className="font-display text-xl font-bold text-flag-navy">Suggested civic moments</h2>
            <p className="text-slate-600 mt-1">Toggle on the ones you'll observe. Each is paired with a content angle to use when drafting.</p>
            <ul className="mt-6 divide-y divide-slate-100" data-testid="calendar-suggested-list">
              {suggested.map((s) => (
                <li key={s.id} className="py-4 flex items-start justify-between gap-4" data-testid={`calendar-suggested-row-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display font-bold text-flag-navy">{s.label}</span>
                      <span className="text-xs font-semibold text-slate-500">{formatLongDate(s.next_date)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.theme}</p>
                  </div>
                  <Switch
                    checked={s.enabled}
                    onCheckedChange={() => toggleSuggested(s)}
                    data-testid={`calendar-suggested-toggle-${s.id}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>
      </Tabs>

      <EntryDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        initial={editing}
        onSave={saveEntry}
      />
    </div>
  );
}

function EntryRow({ entry, onDraft, onEdit, onDelete }) {
  const isSuggested = entry.kind === "suggested";
  return (
    <li
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3"
      data-testid={`calendar-entry-row-${entry.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{formatLongDate(entry.date)}</span>
          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${isSuggested ? "bg-flag-navy/10 text-flag-navy" : "bg-flag-red/10 text-flag-red"}`}>
            {isSuggested ? "Civic" : "Custom"}
          </span>
        </div>
        <div className="mt-1 font-display text-lg font-bold text-flag-navy">{entry.label}</div>
        {entry.theme && <p className="text-sm text-slate-600 mt-1">{entry.theme}</p>}
        {entry.notes && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap leading-relaxed">{entry.notes}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => onDraft(entry)}
          data-testid={`calendar-draft-${entry.id}`}
          className="h-9 px-3 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Draft a post for this
        </Button>
        {entry.kind === "custom" && (
          <Button
            variant="outline"
            onClick={onEdit}
            data-testid={`calendar-edit-${entry.id}`}
            className="h-9 px-3 border-2 font-semibold"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              data-testid={`calendar-delete-${entry.id}`}
              className="h-9 px-3 text-slate-600 hover:text-flag-red hover:bg-red-50 font-semibold"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {entry.kind === "suggested" ? "Remove this date from your calendar?" : "Delete this date?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {entry.kind === "suggested"
                  ? "You can toggle it back on from the Suggested tab any time."
                  : "This date will be permanently removed."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`calendar-delete-cancel-${entry.id}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(entry.id)}
                data-testid={`calendar-delete-confirm-${entry.id}`}
                className="bg-flag-red hover:bg-flag-red-hover"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
